import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  REPORTING_LE_LENS_CERTIFICATION_REQUESTS_PATH,
  reportingLeLensCertificationPath,
} from '~/consts/reportingLeLensApi';

import { request } from '~/services/common';

import type { Lens, LensCertification, LensHome } from '~/types/leLens';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { reportingLeLensQueryKeys } from './useGetReportingLeLens';

export type LensCertificationAction = 'request' | 'certify' | 'decline' | 'remove';

export type LensCertificationRequest = { lens: Lens; home: LensHome; certification: LensCertification; changes: Array<string> };

export const lensCertificationQueryKeys = {
  requests: () => [...reportingLeLensQueryKeys.all, 'certification-requests'] as const,
};

export function refreshCertifiedShelves(client: QueryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() }),
    client.invalidateQueries({ queryKey: lensCertificationQueryKeys.requests() }),
  ]);
}

export function useGetLensCertificationRequests(enabled: boolean) {
  return useQuery({
    queryKey: lensCertificationQueryKeys.requests(),
    queryFn: async ({ signal }) =>
      (
        (await request(resolveReportingLeLensUrl(REPORTING_LE_LENS_CERTIFICATION_REQUESTS_PATH), { signal })) as {
          requests: Array<LensCertificationRequest>;
        }
      ).requests,
    enabled,
  });
}

export function useActOnLensCertification() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async (input: { lensId: string; action: LensCertificationAction; note?: string; meaning?: string }) =>
      (await request(resolveReportingLeLensUrl(reportingLeLensCertificationPath(input.lensId)), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: input.action, note: input.note, meaning: input.meaning }),
      })) as { certification: LensCertification },
    onSuccess: (_result, { lensId }) => {
      queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.detail(lensId) });
      refreshCertifiedShelves(queryClient);
    },
    onError: (error: Error) => enqueueSnackbar(error.message || 'Something went wrong', { variant: 'error' }),
    retry: false,
  });
}
